import { Link } from 'react-router-dom';
import { CheckCircle2, MapPinned, ShieldCheck, Sparkles } from 'lucide-react';
import { SiteLogo } from '@/components/common/SiteLogo';
import { useI18n } from '@/hooks/useI18n';
import { useSiteName } from '@/hooks/useSiteName';
import { cn } from '@/lib/utils';

interface AuthHeroPanelProps {
  compact?: boolean;
  onNavigateHome?: () => void;
}

export function AuthHeroPanel({
  compact = false,
  onNavigateHome,
}: AuthHeroPanelProps) {
  const { t } = useI18n();
  const siteName = useSiteName();
  const benefits = [
    { icon: MapPinned, text: t('auth.heroBenefitPackages') },
    { icon: ShieldCheck, text: t('auth.heroBenefitSecure') },
    { icon: CheckCircle2, text: t('auth.heroBenefitSimple') },
  ];

  return (
    <aside
      className={cn(
        'relative hidden overflow-hidden bg-gradient-to-br from-primary via-primary/90 to-primary/70 text-primary-foreground lg:flex lg:flex-col lg:justify-between',
        compact ? 'min-h-full p-9 xl:p-10' : 'min-h-[720px] p-12',
      )}
    >
      <div
        aria-hidden="true"
        className="absolute -right-24 -top-24 h-72 w-72 rounded-full border border-white/15 bg-white/10"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-32 -left-20 h-80 w-80 rounded-full border border-white/10 bg-black/5"
      />

      <Link
        to="/"
        onClick={onNavigateHome}
        className="relative z-10 inline-flex w-fit text-2xl font-bold text-white"
      >
        <SiteLogo imageClassName="h-10 w-auto brightness-0 invert" />
      </Link>

      <div className={cn('relative z-10 max-w-md', compact ? 'space-y-6' : 'space-y-8')}>
        <div className={cn(compact ? 'space-y-3' : 'space-y-4')}>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-sm font-medium backdrop-blur-sm">
            <Sparkles aria-hidden="true" className="h-4 w-4" />
            {t('auth.heroEyebrow')}
          </div>
          <h2
            className={cn(
              'font-bold leading-tight tracking-tight',
              compact ? 'text-3xl xl:text-4xl' : 'text-4xl',
            )}
          >
            {t('auth.heroTitle')}
          </h2>
          <p
            className={cn(
              'text-white/80',
              compact
                ? 'text-sm leading-6 xl:text-base xl:leading-7'
                : 'text-base leading-7',
            )}
          >
            {t('auth.heroDescription')}
          </p>
        </div>

        <ul className={cn(compact ? 'space-y-3' : 'space-y-4')}>
          {benefits.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-center gap-3 text-sm">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15">
                <Icon aria-hidden="true" className="h-4 w-4" />
              </span>
              {text}
            </li>
          ))}
        </ul>
      </div>

      <p className="relative z-10 text-xs text-white/60">
        {t('auth.copyright', {
          year: String(new Date().getFullYear()),
          siteName,
        })}
      </p>
    </aside>
  );
}
